/**
 * Internal Interviews React Query Hooks (admin surface).
 *
 * The interview period ended 2026-10-07; the user app's public tracker page
 * and its own copy of these hooks (packages/frontend/src/hooks/useInterviews.ts)
 * were removed. This admin-only copy — the only one left — is unchanged.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { interviewsApi } from "@/services/interviews";
import type {
  CreateTeamMemberInput,
  UpdateTeamMemberInput,
} from "@/services/interviews";

export const interviewKeys = {
  all: ["interviews"] as const,
  admin: () => [...interviewKeys.all, "admin"] as const,
  detail: (id: string) => [...interviewKeys.all, "detail", id] as const,
};

/** Admin: fetch full team member list. */
export function useAdminInterviews() {
  return useQuery({
    queryKey: interviewKeys.admin(),
    queryFn: () => interviewsApi.listAdmin(),
    staleTime: 30_000,
  });
}

/** Admin: fetch a single team member by ID. */
export function useAdminInterviewDetail(id: string) {
  return useQuery({
    queryKey: interviewKeys.detail(id),
    queryFn: () => interviewsApi.getAdmin(id),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** Admin: create a team member. */
export function useCreateTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTeamMemberInput) => interviewsApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.all });
    },
  });
}

/** Admin: update a team member. */
export function useUpdateTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTeamMemberInput }) =>
      interviewsApi.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.all });
    },
  });
}

/** Admin: delete a team member. */
export function useDeleteTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => interviewsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.all });
    },
  });
}
